/**
 * Shared Value/Range mode logic for the number-range filter editors
 * (NumberRangeCompactInput + NumberRangeSlider). These two render together for
 * the same persisted filter value, so keeping mode inference and shape
 * conversion in one place prevents them from drifting into inconsistent state.
 *
 * Value shape: { exact } => "value" mode, { min, max } => "range" mode.
 */

export const MODE_OPTIONS = [
  { display: "Value", value: "value" },
  { display: "Range", value: "range" },
];

/**
 * Clamp a value to [lo, hi]. Non-numeric input falls back to the lower bound,
 * so callers can pass raw input safely.
 */
export const clampToBounds = (val, lo, hi) => {
  const num = parseFloat(val);
  if (isNaN(num)) return lo;
  return Math.min(hi, Math.max(lo, num));
};

/**
 * Infer the editor mode from the value shape.
 * @returns {"value" | "range"}
 */
export const getNumberRangeMode = (value) =>
  value?.exact !== undefined ? "value" : "range";

/**
 * Convert the persisted value between modes, clamped to [min, max].
 * "value" => { exact }, "range" => { min, max }.
 */
export const convertNumberRangeValue = (value, newMode, min, max) => {
  if (newMode === "value") {
    return { exact: clampToBounds(value?.min ?? value?.exact ?? min, min, max) };
  }
  return { min: clampToBounds(value?.exact ?? value?.min ?? min, min, max), max };
};
